import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { getLicense, PLAN_LABEL } from '../config/licenseKeys';
import api from '../api/client';
import './Onboarding.css';

const BIZ_TYPES = [
  'Agency', 'IT / Tech', 'Retail', 'Coaching', 'Healthcare',
  'Restaurant', 'Construction', 'Freelancer', 'Other',
];

const TOTAL_STEPS = 5; // 0=login 1=what-you-do 2=company 3=team 4=done

export default function Onboarding() {
  const { theme } = useTheme();
  const { update: updateCompany } = useCompany();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const logoRef = useRef();

  const license = getLicense();
  const planSeats = license ? license.seats : null; // null = unlimited / no license

  const [step, setStep]       = useState(0);
  const [bizType, setBizType] = useState('');
  const [bizOther, setBizOther] = useState('');

  const [co, setCo] = useState(() => {
    let seed = {};
    try { seed = JSON.parse(localStorage.getItem('ryzenos_company') || '{}') || {}; } catch {}
    return {
      contactName: seed.contactName || '',
      name: seed.name || '',
      users: planSeats != null ? String(planSeats) : (seed.users || ''),
      gstin: seed.gstin || '',
      email: seed.email || '',
      phone: seed.phone || '',
      website: seed.website || '',
      address: seed.address || '',
      city: seed.city || '',
      state: seed.state || '',
      pincode: seed.pincode || '',
      country: 'India',
      logo: seed.logo || '',
      services: [],
      bank: seed.bank || { accName: '', accNumber: '', bank: '', branch: '', ifsc: '' },
      username: seed.username || '',
      password: '',
    };
  });
  const setCof  = (k, v) => setCo(p => ({ ...p, [k]: v }));
  const setBank = (k, v) => setCo(p => ({ ...p, bank: { ...p.bank, [k]: v } }));

  const [svcInput, setSvcInput] = useState('');
  const addService = () => {
    const v = svcInput.trim();
    if (!v || co.services.includes(v)) { setSvcInput(''); return; }
    setCo(p => ({ ...p, services: [...p.services, v] }));
    setSvcInput('');
  };
  const removeService = (s) => setCo(p => ({ ...p, services: p.services.filter(x => x !== s) }));

  const onLogo = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCof('logo', reader.result);
    reader.readAsDataURL(f);
  };

  // Team members -- capped to the plan's seat limit
  const [email, setEmail] = useState('');
  const [members, setMembers] = useState([]);
  const [teamMsg, setTeamMsg] = useState('');
  const seatLimit = planSeats; // null = unlimited
  const addMember = () => {
    if (!email.trim()) return;
    if (seatLimit != null && members.length + 1 >= seatLimit) {
      setTeamMsg(`Your ${license ? PLAN_LABEL[license.plan] : ''} plan includes ${seatLimit} member${seatLimit>1?'s':''} (you + team). Upgrade to add more.`);
      return;
    }
    setMembers(p => [...p, { email: email.trim(), role: 'User' }]);
    setEmail('');
    setTeamMsg('');
  };
  const removeMember = (i) => setMembers(p => p.filter((_, idx) => idx !== i));

  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activated, setActivated] = useState(false);

  const persistLocal = (profile) => {
    updateCompany(profile);
    localStorage.setItem('ryzenos_onboarded', 'true');
    localStorage.setItem('ryzenos_company', JSON.stringify(profile));
    localStorage.setItem('ryzenos_company_profile', JSON.stringify(profile));
    if (Array.isArray(co.services) && co.services.length) {
      localStorage.setItem('ryzenos_services', JSON.stringify(co.services));
    }
    localStorage.removeItem('ryzenos_activating');
  };

  const finalBiz = () => (bizType === 'Other' ? (bizOther.trim() || 'Other') : (bizType || 'Other'));

  // Step 0 login guard
  const loginValid = () => {
    if (!co.username.trim()) { setErr('Choose a username.'); return false; }
    if (!co.password || co.password.length < 6) { setErr('Password must be at least 6 characters.'); return false; }
    setErr(''); return true;
  };

  const handleLaunch = async () => {
    const employees = members.map(m => ({ ...m, role: 'User' }));
    const profile = { ...co, bizType: finalBiz(), country: 'India', members: employees };

    if (isAuthenticated) {
      persistLocal(profile);
      navigate('/dashboard');
      return;
    }
    if (!co.username.trim() || !co.password.trim() || co.password.length < 6) {
      setStep(0); setErr('Choose a username and a password of at least 6 characters.'); return;
    }
    const lic = getLicense();
    setSubmitting(true); setErr('');
    try {
      const res = await api.auth.activate({
        key: lic && lic.key,
        company: {
          name: co.name, contactName: co.contactName, email: co.email, phone: co.phone,
          gstin: co.gstin, businessType: finalBiz(), address: co.address, city: co.city,
          state: co.state, pincode: co.pincode, logo: co.logo, bank: co.bank, services: co.services,
        },
        admin: { name: co.contactName || co.username || co.name, username: co.username.trim(), password: co.password },
      });
      api.setToken(res.token);
      const initials = (res.user.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
      sessionStorage.setItem('ryzenos_session', JSON.stringify({ ...res.user, initials }));
      persistLocal(profile);
      localStorage.removeItem('ryzenos_license');
      setSubmitting(false);
      setActivated(true);
      setTimeout(() => window.location.assign('/dashboard'), 2600);
    } catch (e) {
      setSubmitting(false);
      setErr(e.message || 'Activation failed. Please try again.');
    }
  };

  const seatHint = seatLimit == null ? 'Unlimited members' : `${members.length + 1} of ${seatLimit} members`;

  if (activated) {
    return (
      <div className="ob-wrap" data-theme={theme}>
        <div className="ob-glow" />
        <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{textAlign:'center',maxWidth:420,animation:'obPop .4s ease'}}>
            <div style={{width:72,height:72,borderRadius:'50%',background:'rgba(34,197,94,.15)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div style={{fontSize:24,fontWeight:800,marginBottom:8}}>Workspace activated!</div>
            <div style={{opacity:.7,fontSize:14,lineHeight:1.6,marginBottom:22}}>Your key is now linked to <b>{co.name || 'your company'}</b>. Taking you into your workspace…</div>
            <button className="ob-next" onClick={()=>window.location.assign('/dashboard')}>Enter workspace</button>
          </div>
        </div>
        <style>{`@keyframes obPop{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}`}</style>
      </div>
    );
  }

  return (
    <div className="ob-wrap" data-theme={theme}>
      <div className="ob-glow" />
      <div className="ob-inner">
        <div className="ob-prog">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <React.Fragment key={i}>
              <div className={`ob-dot${step===i?' active':step>i?' done':''}`} />
              {i < TOTAL_STEPS - 1 && <div className={`ob-gap${step>i?' done':''}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="ob-step" key={step}>
          {/* Step 0: Create login */}
          {step === 0 && (
            <>
              <div className="ob-badge"><svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg></div>
              <div className="ob-tag">Get started{license ? ` · ${PLAN_LABEL[license.plan]} plan` : ''}</div>
              <div className="ob-title">Create your login</div>
              <div className="ob-sub-note" style={{marginTop:-16,marginBottom:18}}>You'll use this to sign in to your workspace. Everything else can be added later.</div>
              <div className="ob-co-form">
                <div className="ob-field-grp ob-field-grp--full">
                  <label className="ob-field-lbl">Username *</label>
                  <input className="ob-field-inp" placeholder="e.g. yourname" value={co.username} autoFocus
                    onChange={e=>setCof('username', e.target.value.replace(/\s/g,''))} autoComplete="username" />
                </div>
                <div className="ob-field-grp ob-field-grp--full">
                  <label className="ob-field-lbl">Password *</label>
                  <input className="ob-field-inp" type="password" placeholder="At least 6 characters" value={co.password}
                    onChange={e=>setCof('password', e.target.value)} autoComplete="new-password"
                    onKeyDown={e=>{ if(e.key==='Enter' && loginValid()) setStep(1); }} />
                </div>
                {err && <div className="ob-team-msg" style={{ color: '#ff6b6b', borderColor: 'rgba(255,107,107,.3)', background: 'rgba(255,107,107,.08)' }}>{err}</div>}
              </div>
              <div className="ob-footer">
                <span className="ob-hint">Step 1 of {TOTAL_STEPS}</span>
                <button className="ob-next" onClick={() => { if (loginValid()) setStep(1); }}>Continue</button>
              </div>
            </>
          )}

          {/* Step 1: What you do */}
          {step === 1 && (
            <>
              <div className="ob-badge"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M14.5 9.5l-2 5-5 2 2-5z"/></svg></div>
              <div className="ob-tag">About you</div>
              <div className="ob-title">What do you do?</div>
              <div className="ob-biz-grid">
                {BIZ_TYPES.map(b => (
                  <div key={b} className={`ob-biz-card${bizType===b?' selected':''}`} onClick={() => setBizType(b)}>
                    <div className="ob-biz-tick">✓</div>
                    <div className="ob-biz-name">{b}</div>
                  </div>
                ))}
              </div>
              {bizType === 'Other' && (
                <input className="ob-inp" style={{marginTop:12}} placeholder="Tell us what you do" value={bizOther} autoFocus
                  onChange={e=>setBizOther(e.target.value)} />
              )}
              <div className="ob-footer">
                <button className="ob-back" onClick={() => setStep(0)}>Back</button>
                <button className="ob-next" onClick={() => setStep(2)}>Continue</button>
              </div>
            </>
          )}

          {/* Step 2: Company basics (minimal, optional) */}
          {step === 2 && (
            <>
              <div className="ob-badge"><svg viewBox="0 0 24 24"><rect x="6" y="4" width="12" height="16" rx="1"/><path d="M10 8h1M13 8h1M10 12h1M13 12h1M10 16h4"/></svg></div>
              <div className="ob-tag">Company</div>
              <div className="ob-title">A few company basics</div>
              <div className="ob-sub-note" style={{marginTop:-16,marginBottom:18}}>Add address, bank, and logo later from Settings.</div>
              <div className="ob-co-form">
                <div className="ob-field-grp ob-field-grp--full">
                  <label className="ob-field-lbl">Company name</label>
                  <input className="ob-field-inp" placeholder="Your company" value={co.name} onChange={e=>setCof('name',e.target.value)} />
                </div>
                <div className="ob-field-grp ob-field-grp--full">
                  <label className="ob-field-lbl">Business email</label>
                  <input className="ob-field-inp" type="email" placeholder="hello@company.in" value={co.email} onChange={e=>setCof('email',e.target.value)} />
                </div>
              </div>
              <div className="ob-footer">
                <button className="ob-back" onClick={() => setStep(1)}>Back</button>
                <div style={{display:'flex',gap:10,alignItems:'center'}}>
                  <button className="ob-skip-link" onClick={() => setStep(3)}>Skip for now</button>
                  <button className="ob-next" onClick={() => setStep(3)}>Continue</button>
                </div>
              </div>
            </>
          )}

          {/* Step 3: Team (optional) */}
          {step === 3 && (
            <>
              <div className="ob-badge"><svg viewBox="0 0 24 24"><circle cx="9" cy="9" r="3"/><path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5"/><path d="M16 6.5a3 3 0 0 1 0 6"/><path d="M20.5 20c0-2.3-1.7-4-3.8-4.6"/></svg></div>
              <div className="ob-tag">Your team · {seatHint}</div>
              <div className="ob-title">Who's joining you?</div>
              <div className="ob-sub-note" style={{marginTop:-16,marginBottom:18}}>You're the owner. Add teammates by email — they join as employees.</div>
              <input className="ob-inp" placeholder="teammate@company.com" value={email}
                onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addMember()} />
              <button className="ob-add-btn" onClick={addMember}>
                {seatLimit != null && members.length + 1 >= seatLimit ? `Plan limit reached (${seatLimit})` : 'Add member'}
              </button>
              {teamMsg && <div className="ob-team-msg">{teamMsg}</div>}
              <div className="ob-members">
                <div className="ob-member">
                  <div className="ob-member-info">
                    <div className="ob-av">{((co.contactName||co.username||'Y')[0]||'Y').toUpperCase()}</div>
                    <span className="ob-member-name">{co.contactName || co.username || 'You'}{co.email && <span style={{opacity:.5,fontWeight:400}}> · {co.email}</span>}</span>
                  </div>
                  <span className="ob-role-tag">Owner (you)</span>
                </div>
                {members.map((m, i) => (
                  <div key={i} className="ob-member">
                    <div className="ob-member-info">
                      <div className="ob-av">{(m.email[0]||'?').toUpperCase()}</div>
                      <span className="ob-member-name">{m.email}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span className="ob-role-tag">Employee</span>
                      <span className="ob-skip-link" onClick={()=>removeMember(i)}>Remove</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ob-footer">
                <button className="ob-back" onClick={() => setStep(2)}>Back</button>
                <div style={{display:'flex',gap:10,alignItems:'center'}}>
                  <button className="ob-skip-link" onClick={() => setStep(4)}>Skip for now</button>
                  <button className="ob-next" onClick={() => setStep(4)}>Continue</button>
                </div>
              </div>
            </>
          )}

          {/* Step 4: Done */}
          {step === 4 && (
            <>
              <div className="ob-launch">
                <div className="ob-launch-title">You're all set{co.name ? `, ${co.name}` : ''}</div>
                <div className="ob-launch-sub">Your workspace is ready to go</div>
              </div>
              <div className="ob-review">
                {[
                  { label: 'Login',         val: co.username || '—' },
                  { label: 'What you do',   val: finalBiz() },
                  { label: 'Company',       val: co.name || 'Add later' },
                  { label: 'Plan',          val: license ? PLAN_LABEL[license.plan] : 'Trial' },
                  { label: 'Team',          val: seatLimit == null ? `${members.length + 1} members` : `${members.length + 1} of ${seatLimit}` },
                ].map(r => (
                  <div key={r.label} className="ob-review-item">
                    <span className="ob-review-label">{r.label}</span>
                    <span className="ob-review-val">{r.val}</span>
                  </div>
                ))}
              </div>
              {err && <div className="ob-team-msg" style={{ color: '#ff6b6b', borderColor: 'rgba(255,107,107,.3)', background: 'rgba(255,107,107,.08)', marginTop: 14 }}>{err}</div>}
              <div className="ob-footer">
                <button className="ob-back" onClick={() => setStep(3)} disabled={submitting}>Back</button>
                <button className="ob-next" onClick={handleLaunch} disabled={submitting}>{submitting ? 'Setting up…' : (isAuthenticated ? 'Launch workspace' : 'Finish setup')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
